import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { PostVideo } from "../../../../cores/socialPost/interfaces/postVideo";
import { SocialProviderRules } from "../../../../cores/socialPost/config";
import AreYouSure from "../../AreYouSure";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faCloudUploadAlt,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { VideoBankModal } from "../VideoBankModal";
import { VideoPreview } from "../creator/VideoPreview";
import { COLOR_PRIMARY } from "../../../../constants/theme";
import { useGlobalCore } from "../../../../cores/globals";
import { ADD_SOCIAL_VIDEO_MODAL } from "../../../../constants/modal.constant";
import { useMeCore } from "../../../../cores/me";
import { Container, Row, Col, Button } from "reactstrap";
import { getBase64 } from "../../../../cores/utils";
import "./ModernVideoForm.scss";

interface Props {
  singleVideo?: boolean;
}

type VideoSource = 'upload' | 'bank' | null;

export default function ModernVideoForm(props: Props) {
  const { singleVideo = false } = props;
  const intl = useIntl(); 
  const {
    addVideo,
    addVideoDuration,
    addVideoSize,
    video,
    images,
    removeAllImages,
    removePostAllMedia,
    selectedPost,
    socialProviders,
  } = useSocialPostCore();
  const { turnOffLoading, turnOnLoading, turnOffModal, isLoading } = useGlobalCore();
  const { companyPlan } = useMeCore();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSource, setSelectedSource] = useState<VideoSource>(null);
  const [showVideoBankModal, setShowVideoBankModal] = useState(false);

  const maxSizeVideo = useMemo(() => {
    if (socialProviders.length === 0) {
      return 50 * 1024 * 1024; // Default 50MB for videos
    }
    const sizes = socialProviders
      .map(({ id }) => SocialProviderRules[id]?.maxVideoSize)
      .filter(size => size !== undefined);
    
    if (sizes.length === 0) {
      return 50 * 1024 * 1024; // Default 50MB for videos
    }
    
    return Math.max(...sizes) * 1024 * 1024;
  }, [socialProviders]);

  const removeImages = useCallback(() => {
    removeAllImages();
    if (selectedPost) {
      removePostAllMedia();
    }
  }, [removeAllImages, selectedPost, removePostAllMedia]);

  const handleVideoBankClick = () => {
    setShowVideoBankModal(true);
  };

  const handleUploadClick = () => {
    // Open file browser directly
    setSelectedSource('upload');
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []).filter(file => 
      file.type.startsWith('video/')
    );
    
    if (files.length > 0) {
      const file = files[0];

      // Convert file to data URL so it can be correctly sent as multipart/form-data
      const dataUrl = (await getBase64(file)) as string;

      // Create PostVideo object
      const postVideo: PostVideo = {
        id: Date.now().toString(),
        name: file.name,
        size: file.size,
        type: file.type,
        data: dataUrl,
        lastModified: file.lastModified,
        duration: 0, // Will be updated when video loads
        width: 0, // Will be updated when video loads
        height: 0, // Will be updated when video loads
      };

      // Add video to state
      addVideo(postVideo);
    }
    
    // Reset input value to allow selecting the same file again
    if (e.target) {
      e.target.value = '';
    }
  };

  const videoSourceOptions = [
    {
      id: 'upload' as VideoSource,
      title: 'Subir desde mi dispositivo',
      description: 'Selecciona videos desde tu computadora',
      icon: faCloudUploadAlt,
      color: COLOR_PRIMARY,
      action: () => {
        setSelectedSource('upload');
        handleUploadClick();
      }
    },
    {
      id: 'bank' as VideoSource,
      title: intl.formatMessage({ id: "app.socialMediaPost.videoBank" }),
      description: 'Videos gratuitos de alta calidad',
      icon: faVideo,
      color: '#dc3545',
      action: () => {
        setSelectedSource('bank');
        handleVideoBankClick();
      }
    }
  ];

  return (
    <div className="modern-video-form">
      <Container fluid className="h-100">
        <Row className="h-100">
          <Col xs="12" className="d-flex flex-column h-100">
            {/* Header */}
            <div className="video-form-header text-center py-3">
              <h4>Selecciona un video</h4>
              <p className="text-muted">Elige cómo quieres agregar tu video</p>
            </div>

            {/* Video Source Options */}
            <div className="video-source-options mb-4">
              <Row>
                {videoSourceOptions.map((option) => (
                  <Col xs="12" md="6" key={option.id} className="mb-3">
                    <div
                      className={`video-source-card ${selectedSource === option.id ? 'selected' : ''}`}
                      onClick={option.action}
                    >
                      <div className="card-icon">
                        <FontAwesomeIcon 
                          icon={option.icon} 
                          style={{ color: option.color }}
                        />
                      </div>
                      <div className="card-content">
                        <h5>{option.title}</h5>
                        <p className="text-muted">{option.description}</p>
                      </div>
                      {selectedSource === option.id && (
                        <div className="selected-indicator">
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Hidden Native File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              multiple={!singleVideo}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {/* Video Preview Area */}
            <div className="video-preview-section flex-grow-1 d-flex flex-column">
              {(images.length > 0 ||
                (selectedPost?.post.type === SOCIAL_POST_TYPES.IMAGE &&
                  selectedPost.media.length > 0)) && (
                <AreYouSure
                  message="Ya tienes imágenes subidas, si subes un video perderás dichas imágenes"
                  onClose={() => turnOffModal(ADD_SOCIAL_VIDEO_MODAL)}
                  onSubmit={removeImages}
                />
              )}
              
              {images.length === 0 &&
                (!selectedPost ||
                  selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO ||
                  (selectedPost?.post.type === SOCIAL_POST_TYPES.IMAGE &&
                    !selectedPost.media.length)) && (
                  <div className="video-preview-container">
                    {video ? (
                      <div className="video-preview-wrapper">
                        <VideoPreview
                          video={video.data}
                          type={video.type}
                          onLoadedMetadata={(video) => {
                            addVideoDuration(video.duration);
                            addVideoSize(video.videoWidth, video.videoHeight);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="no-video-placeholder">
                        <FontAwesomeIcon
                          icon={faVideo}
                          size="4x"
                          className="mb-3"
                          color="#ccc"
                        />
                        <h5 className="text-muted">No has seleccionado ningún video</h5>
                        <p className="text-muted">Usa las opciones de arriba para agregar un video</p>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Video Bank Modal */}
      <VideoBankModal
        show={showVideoBankModal}
        onHide={() => setShowVideoBankModal(false)}
      />

    </div>
  );
}
